import ReactDOM from "react-dom/client";
import { configureMediaUrls } from "@gearghost/media";
import { requireEnv } from "./utils/env.js";
import App from "./app";
// Stripe's script on every page, on purpose (Tyler, 2026-10-21): Stripe's
// fraud detection watches how a renter browses, not only the checkout, and
// GearGhost rents out gear worth thousands. Its README: "ensure that Stripe.js is loaded on every page, not
// just your checkout page". Importing the package adds the script; everything
// else asks for Stripe through getStripePromise (helperFunctions.jsx).
import "@stripe/stripe-js";

// Media URLs (profile pics, listing images) build against the CDN — must
// be configured before the first render (buildImageUrl fails fast).
configureMediaUrls({ cdnBaseUrl: requireEnv("VITE_CLOUDFRONT_URL") });
// Self-hosted Poppins font (faster than Google Fonts CDN)
import "@fontsource/poppins/100.css";
import "@fontsource/poppins/200.css";
import "@fontsource/poppins/300.css";
import "@fontsource/poppins/400.css";
import "@fontsource/poppins/500.css";
import "@fontsource/poppins/600.css";
import "@fontsource/poppins/700.css";
import "@fontsource/poppins/800.css";
import "@fontsource/poppins/900.css";
// Critical CSS - load immediately
import "bootstrap/dist/css/bootstrap.min.css";
import "./styles/main.scss";
import "./dashboard.scss";
// The shared date picker's variables, then web's theme on them.
import "@gearghost/date-picker/styles/tokens.css";
import "./styles/datePicker.tokens.css";
// The shared listing pieces' variables, then web's theme on them.
import "@gearghost/listing-ui/styles/tokens.css";
import "./styles/listingUi.tokens.css";
// The shared file viewer's variables, then web's theme on them.
import "@gearghost/file-viewer/styles/tokens.css";
import "./styles/fileViewer.tokens.css";
import { Provider } from "react-redux";
import { store } from '@/redux/store';
import { authApi } from '@/redux/api/authApi';
import { userApi } from '@/redux/api/userApi';
import { authController } from '@/utils/authController';
import { restoreSession } from '@/utils/tokenRefresh.js';
import { Toaster, ToastBar } from "react-hot-toast";

// Initialize authController with Redux dependencies (avoids circular imports)
authController.init({ store, authApi, userApi });
import toast from "react-hot-toast";
import { toastSuccess, toastError, toastWarning, toastLoading, toastInfo, toastPromise, TOAST_ICON_SIZE, TOAST_BASE_STYLES } from "@/utils/toast.jsx";
import toastStyles from './index.module.scss';
import { RefreshCoordinator } from '@/component/auth/RefreshCoordinator.jsx';
import SupportChatWidget from '@common/support/SupportChatWidget';
import SudoPasswordPrompt from '@/component/auth/SudoPasswordPrompt';
import { initDevErrorSimulation } from '@/utils/devErrorSimulation';
import { initDevToolsConsole } from '@/utils/devToolsConsole';
import { reloadOncePerBuild } from '@/utils/reloadOncePerBuild.js';

// Initialize dev error simulation utility (window.__gearghost_errors)
initDevErrorSimulation();

// Initialize dev tools console utility (window.__gearghost_devtools)
initDevToolsConsole();

// Defer Bootstrap JS - not needed for initial render
// Load it after the initial paint to improve TTI (Time to Interactive)
if (typeof window !== 'undefined') {
  // Use requestIdleCallback for non-critical JS
  const loadBootstrapJS = () => {
    import("bootstrap/dist/js/bootstrap.bundle.min.js").catch(() => {
      // Silently fail if already loaded or error
    });
  };
  
  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(loadBootstrapJS, { timeout: 1000 });
  } else {
    setTimeout(loadBootstrapJS, 100);
  }
}

// Safety net for Vite's automatic chunk preloading failures.
// When Vite preloads transitive dependencies (CSS, shared JS chunks) and they fail,
// this event fires. We reload to get fresh chunks, at most once per build, so a
// file the build itself cannot load never loops the page (reloadOncePerBuild).
window.addEventListener('vite:preloadError', () => {
  reloadOncePerBuild();
});

// Handle errors globally - check for chunk loading errors first, then suppress extension errors
window.addEventListener("error", (event) => {
  const errorMessage = event.message || event.error?.message || "";
  const errorStack = event.error?.stack || "";
  
  // Check if it's a chunk loading error first
  const isChunkError = 
    errorMessage?.includes('Loading chunk') ||
    errorMessage?.includes('Failed to fetch') ||
    event.error?.name === 'ChunkLoadError' ||
    event.filename?.includes('chunk');
  
  if (isChunkError) {
    console.error("[Global Error Handler] Chunk loading error detected:", {
      message: event.message,
      stack: errorStack,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error,
      timestamp: new Date().toISOString(),
      url: window.location.href,
    });
    
    // Don't prevent default - let the error boundary handle it
    // But log it for monitoring
    return;
  }
  
  // Note: React DevTools errors should be fixed by initializing the hook object above
  // If we still see these errors, it means the initialization didn't work
  
  // Filter out Chrome extension messaging errors
  if (
    errorMessage &&
    (errorMessage.includes("message channel closed") ||
      errorMessage.includes("asynchronous response") ||
      errorMessage.includes("Extension context invalidated") ||
      errorMessage.includes("A listener indicated an asynchronous response"))
  ) {
    event.preventDefault();
    return false;
  }
});

// Suppress unhandled promise rejections from browser extensions
window.addEventListener("unhandledrejection", (event) => {
  // Check if it's a chunk loading error
  const isChunkError = 
    event.reason?.message?.includes('Loading chunk') ||
    event.reason?.name === 'ChunkLoadError' ||
    (typeof event.reason === 'string' && event.reason.includes('chunk'));
  
  if (isChunkError) {
    console.error("[Global Error Handler] Unhandled chunk loading rejection:", {
      reason: event.reason,
      timestamp: new Date().toISOString(),
      url: window.location.href,
    });
    
    // Don't prevent default - let the error boundary handle it
    return;
  }
  
  const errorMessage =
    typeof event.reason === "string"
      ? event.reason
      : event.reason?.message || event.reason?.toString() || "";

  // Note: React DevTools errors should be fixed by initializing the hook object above
  // If we still see these errors, it means the initialization didn't work
  
  if (
    errorMessage &&
    (errorMessage.includes("message channel closed") ||
      errorMessage.includes("asynchronous response") ||
      errorMessage.includes("Extension context invalidated") ||
      errorMessage.includes("A listener indicated an asynchronous response"))
  ) {
    event.preventDefault();
    return false;
  }
});

// Dev utility: Expose toast functions to window for easy testing
// Now uses centralized toast utility for consistency
if (import.meta.env.DEV && typeof window !== 'undefined') {
  window.__gearghost_toast = {
    success: (msg = "Success message") => toastSuccess(msg),
    error: (msg = "Error message") => toastError(msg),
    warning: (msg = "Warning message") => toastWarning(msg),
    loading: (msg = "Loading...") => toastLoading(msg),
    info: (msg = "Info message") => toastInfo(msg),
    promise: () => {
      const myPromise = new Promise((resolve) => {
        setTimeout(() => resolve("Promise resolved!"), 2000);
      });
      toastPromise(myPromise, {
        loading: 'Loading...',
        success: 'Got the data',
        error: 'Error when fetching',
      });
    },
    // Legacy functions - maintained for backward compatibility
    customError: (msg = "Custom error message") => toastError(msg),
    customWarning: (msg = "Warning message") => toastWarning(msg),
    // Show all toast types at once for testing
    showAll: () => {
      console.log('%c🎨 Showing all toast types...', 'color: var(--semantic-brand-primary); font-size: 14px; font-weight: bold;');
      toastSuccess("Success! This is a success message with a circle checkmark icon.");
      setTimeout(() => {
        toastError("Error! This is an error message with a circle X icon.");
      }, 500);
      setTimeout(() => {
        toastWarning("Warning! This is a warning message with a triangle exclamation icon.");
      }, 1000);
      setTimeout(() => {
        toastLoading("Loading... This is a loading message.");
      }, 1500);
      setTimeout(() => {
        toastInfo("Info! This is a longer info message that should wrap to multiple lines. It contains enough text to demonstrate how the toast handles word wrapping and ensures that long messages display properly across four lines of text.");
      }, 2000);
    },
    help: () => {
      console.log(`
🎨 GearGhost Toast Dev Utility

Available functions:
  window.__gearghost_toast.success("Message")     - Show success toast
  window.__gearghost_toast.error("Message")       - Show error toast
  window.__gearghost_toast.warning("Message")    - Show warning toast (custom)
  window.__gearghost_toast.loading("Message")     - Show loading toast
  window.__gearghost_toast.info("Message")        - Show info toast
  window.__gearghost_toast.promise()              - Show promise toast
  window.__gearghost_toast.showAll()              - Show all toast types at once
  window.__gearghost_toast.customError("Message") - Show custom error toast
  window.__gearghost_toast.customWarning("Msg")   - Show custom warning toast
  window.__gearghost_toast.help()                 - Show this help

Keyboard shortcuts (when page is focused):
  Ctrl/Cmd + Shift + S  - Success toast
  Ctrl/Cmd + Shift + E  - Error toast
  Ctrl/Cmd + Shift + W  - Warning toast (custom)
  Ctrl/Cmd + Shift + L  - Loading toast
      `);
    }
  };

  // Keyboard shortcuts for quick testing
  window.addEventListener('keydown', (e) => {
    // Ctrl/Cmd + Shift + S = Success
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'S') {
      e.preventDefault();
      window.__gearghost_toast.success("Success! This is a success message.");
    }
    // Ctrl/Cmd + Shift + E = Error
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'E') {
      e.preventDefault();
      window.__gearghost_toast.error("Error! This is an error message.");
    }
    // Ctrl/Cmd + Shift + W = Warning
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'W') {
      e.preventDefault();
      window.__gearghost_toast.warning("Warning! This is a warning message.");
    }
    // Ctrl/Cmd + Shift + L = Loading
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'L') {
      e.preventDefault();
      window.__gearghost_toast.loading("Loading...");
    }
  });

  // Log help message on first load
  console.log('%c🎨 GearGhost Toast Dev Utility', 'color: var(--semantic-brand-primary); font-size: 16px; font-weight: bold;');
  console.log('%cType window.__gearghost_toast.help() for available functions', 'color: var(--semantic-brand-primary);');
  console.log('%cOr use keyboard shortcuts: Ctrl/Cmd+Shift+S/E/W/L', 'color: var(--semantic-brand-primary);');
}

// Toast dismiss behavior: Toasts now only dismiss on click, not on hover
// react-hot-toast provides click-to-dismiss by default

const root = ReactDOM.createRoot(document.getElementById("root"));
// The access token lives in memory, so a page load asks for its session
// before the first render: every component mounts signed in or
// signed out, never signed out for a moment and then in. A browser that
// never signed in asks nothing and renders at once. `finally`: the page
// renders whatever the answer, and an unexpected throw still surfaces.
restoreSession().finally(() => root.render(
  // <React.StrictMode>
  <Provider store={store}>
    <Toaster 
      position="bottom-left" 
      containerStyle={{
        bottom: 20,
        left: 20,
      }}
      toastOptions={{
        duration: 6000, // 6 seconds
        className: 'gearghost-toast', // Custom class for easier targeting
        style: {
          ...TOAST_BASE_STYLES,
          background: 'var(--semantic-grey-tertiary)', // #3d3f3f - matches search box
        },
        success: {
          className: 'gearghost-toast', // Custom class for easier targeting
          icon: (
            <svg
              width={TOAST_ICON_SIZE}
              height={TOAST_ICON_SIZE}
              fill="none"
              stroke="var(--semantic-brand-primary)"
              viewBox="0 0 24 24"
              style={{
                width: `${TOAST_ICON_SIZE}px`,
                height: `${TOAST_ICON_SIZE}px`,
                minWidth: `${TOAST_ICON_SIZE}px`,
                minHeight: `${TOAST_ICON_SIZE}px`,
                flexShrink: 0,
              }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          ),
          iconTheme: {
            primary: 'var(--semantic-brand-primary)',
            secondary: 'var(--semantic-grey-tertiary)',
          },
          style: {
            ...TOAST_BASE_STYLES,
            background: 'color-mix(in srgb, var(--semantic-grey-tertiary) 90%, var(--semantic-brand-primary) 10%)', // Grey-green tint for success
          },
        },
        error: {
          className: 'gearghost-toast', // Custom class for easier targeting
          icon: (
            <svg
              width={TOAST_ICON_SIZE}
              height={TOAST_ICON_SIZE}
              fill="none"
              stroke="var(--semantic-status-red)"
              viewBox="0 0 24 24"
              style={{
                width: `${TOAST_ICON_SIZE}px`,
                height: `${TOAST_ICON_SIZE}px`,
                minWidth: `${TOAST_ICON_SIZE}px`,
                minHeight: `${TOAST_ICON_SIZE}px`,
                flexShrink: 0,
              }}
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                strokeWidth={2}
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 9l-6 6m0-6l6 6"
              />
            </svg>
          ),
          iconTheme: {
            primary: 'var(--semantic-status-red)',
            secondary: 'var(--semantic-grey-tertiary)',
          },
          style: {
            ...TOAST_BASE_STYLES,
            background: 'color-mix(in srgb, var(--semantic-grey-tertiary) 90%, var(--semantic-status-red) 10%)', // Grey-red tint for error
          },
        },
        loading: {
          iconTheme: {
            primary: 'var(--semantic-brand-primary)',
            secondary: 'var(--semantic-grey-tertiary)',
          },
          style: {
            ...TOAST_BASE_STYLES,
            background: 'color-mix(in srgb, var(--semantic-grey-tertiary) 90%, var(--semantic-brand-primary) 10%)', // Grey-green tint for loading
          },
        },
      }}
      // Limit the number of visible toasts to prevent overwhelming the UI
      // This works in conjunction with the debounce mechanism in axiosInstance
    >
      {(t) => (
        <ToastBar toast={t}>
          {({ icon, message }) => (
            <div 
              onClick={(e) => {
                e.stopPropagation();
                toast.dismiss(t.id);
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                toast.dismiss(t.id);
              }}
              className={toastStyles.toastContent}
            >
              {icon}
              {message}
            </div>
          )}
        </ToastBar>
      )}
    </Toaster>
    <RefreshCoordinator>
      {/* <Router> */}
        <App />
      {/* </Router> */}
      <SupportChatWidget />
      {/* The one password prompt: the axios interceptor asks it when an
          action answers 403 with X-Sudo-Required. */}
      <SudoPasswordPrompt />
    </RefreshCoordinator>
  </Provider>
  // </React.StrictMode>
));
